import { HttpClient } from '@angular/common/http';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
} from '@angular/core';
import { FieldType, FieldTypeConfig } from '@ngx-formly/core';
import {
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  skip,
  startWith,
  Subscription,
  tap,
} from 'rxjs';
import {
  EGenericInputTriggerType,
  GenericInputStatusEnum,
} from '../../../models/irembo-generic-input-status.enum';
import { IEnvironment } from '../../../models/environment.model';
import { v4 as uuidv4 } from 'uuid';
import {
  checkForValidFields,
  populateFormFields,
  updateUrlWithApiGatewayBaseUrl,
  getPopulateReferenceForm,
  subscribeToResetFieldFetchData,
  findFormControl,
  findFormControlInScope,
  handleErrorMapping,
  resetFieldsToPopulate,
} from '../../../../utils/utils/data-fetch-widget-utils';
import { FormStateService } from '../../../services/formly/form-state.service';
import {
  FormlyFieldConfig,
  FormlyValueChangeEvent,
} from '@ngx-formly/core/lib/models';
import { AbstractControl } from '@angular/forms';
import { getParentRepeaterKeyAndItemIndex } from '../../../../utils/utils/formly.utils';
import {
  TransformToArrayConfig,
  TransformFieldMapping,
} from '../../../models/multi-select-data-fetch.model';
import { EIremboFormlyFormStateKeys } from '../../../models/irembo-formly-formstate-keys.enum';

export interface ListenFieldConfig {
  key: string;
  mapTo?: string;
}
@Component({
  selector: 'irembogov-custom-generic-data-fetch',
  templateUrl: './custom-generic-data-fetch.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
})
export class CustomGenericDataFetchComponent
  extends FieldType<FieldTypeConfig>
  implements OnDestroy, AfterViewInit
{
  protected subscriptions = new Subscription();
  statusClass: string | undefined;
  fieldsToPopulate: Record<string, string>[] = [];
  private environment: IEnvironment;
  private isInRepeater = false;
  private currentRepeaterForm: any;
  protected componentId: string;
  private isCurrentlyFetching = false;

  constructor(
    private http: HttpClient,
    private cd: ChangeDetectorRef,
    protected formStateService: FormStateService,
    @Inject('environment') environment: IEnvironment
  ) {
    super();
    this.environment = environment;
    this.componentId = uuidv4();
  }

  protected initializeRepeaterContext() {
    const repeaterKeys = getParentRepeaterKeyAndItemIndex(this.field);
    if (repeaterKeys) {
      this.isInRepeater = true;
      this.currentRepeaterForm = this.form;
    } else {
      this.isInRepeater = false;
      this.currentRepeaterForm = getPopulateReferenceForm(
        this.field,
        this.form
      );
    }
  }

  private isThisExactField(changeField: any): boolean {
    if (!this.isInRepeater) {
      return changeField.key === this.field.key;
    }

    // In repeater context, check the complete field path including all parents
    const getFieldPath = (field: any): string => {
      const path = [];
      let current = field;
      while (current) {
        path.unshift(current.key);
        current = current.parent;
      }
      return path.join('.');
    };

    return getFieldPath(changeField) === getFieldPath(this.field);
  }

  protected findControl(
    targetKey: string,
    searchInRootForm?: boolean
  ): AbstractControl | null {
    // If explicitly told to search in root form, do so
    if (searchInRootForm === true) {
      return findFormControl(this.currentRepeaterForm, targetKey);
    }

    // If explicitly told to search in repeater scope only, do so
    if (searchInRootForm === false && this.isInRepeater) {
      return findFormControlInScope(this.currentRepeaterForm, targetKey);
    }

    // Default behavior: search in scope if in repeater, globally otherwise
    return this.isInRepeater
      ? findFormControlInScope(this.currentRepeaterForm, targetKey)
      : findFormControl(this.currentRepeaterForm, targetKey);
  }

  setupFieldListeners() {
    const { payloadKeyMappings, useMultiplePayloadKeys, payloadKey } =
      this.field.props;
    const debounceMs = this.field.props['debounceTime'] || 500;
    const disableFirstFetch = this.field.props['disableFirstFetchOnRFA'];
    const isInRFAState = this.formStateService.checkIfApplicationInAnRFAState();

    if (
      useMultiplePayloadKeys &&
      !(Array.isArray(payloadKeyMappings) || payloadKeyMappings.length)
    )
      return;

    if (!useMultiplePayloadKeys) {
      const control = this.findControl(payloadKey);
      if (control) {
        let stream = control.valueChanges.pipe(
          startWith(control.value), // Always start with current value
          distinctUntilChanged(),
          debounceTime(debounceMs),
          tap(() => this.formStateService.clearGlobalError(this.field))
        );

        // If disableFirstFetch is true, skip the initial emission
        if (disableFirstFetch && isInRFAState) {
          stream = stream.pipe(skip(1));
        }

        this.subscriptions.add(
          stream.subscribe(value => {
            if (value !== null && value !== undefined) {
              this.onDataFetch(value);
            }
          })
        );
        return;
      }
    }

    const fieldControls: AbstractControl[] = [];
    const fieldConfigs: any[] = [];

    payloadKeyMappings.forEach((mapping: any) => {
      if (!mapping.isStatic && mapping.sourceField) {
        const control = this.findControl(
          mapping.sourceField,
          mapping.searchInRootForm
        );
        if (control) {
          fieldControls.push(control);
          fieldConfigs.push(mapping);
        }
      }
    });

    if (!fieldControls.length) return;

    let fieldObservables;

    if (isInRFAState) {
      fieldObservables = fieldControls.map(control => {
        let stream = control.valueChanges.pipe(
          startWith(control.value), // Always start with current value
          distinctUntilChanged(),
          debounceTime(debounceMs)
        );

        // If disableFirstFetch is true, skip the initial emission
        if (disableFirstFetch) {
          stream = stream.pipe(skip(1));
        }

        return stream;
      });
    } else {
      fieldObservables = fieldControls.map(control => {
        let stream = control.valueChanges.pipe(
          control.value != null && control.value !== ''
            ? startWith(control.value)
            : distinctUntilChanged(),
          debounceTime(debounceMs)
        );

        // If disableFirstFetch is true, skip the initial emission
        if (disableFirstFetch) {
          stream = control.valueChanges.pipe(
            startWith(control.value),
            skip(1),
            distinctUntilChanged(),
            debounceTime(debounceMs)
          );
        }

        return stream;
      });
    }

    this.subscriptions.add(
      combineLatest(fieldObservables)
        .pipe(tap(() => this.formStateService.clearGlobalError(this.field)))
        .subscribe(values => {
          if (values.every(v => v !== null && v !== undefined)) {
            const firstValue = this.findControl(
              fieldConfigs[0].sourceField,
              fieldConfigs[0].searchInRootForm
            )?.value;
            this.onDataFetch(firstValue);
          }
        })
    );
  }

  ngAfterViewInit(): void {
    this.initializeRepeaterContext();

    this.subscriptions.add(
      subscribeToResetFieldFetchData(
        this.field,
        this.formStateService,
        this.fieldsToPopulate,
        this.currentRepeaterForm
      )
    );

    if (
      this.field.props['triggerType'] ===
      EGenericInputTriggerType.FIELD_LISTENER
    ) {
      this.setupFieldListeners();
    }

    this.subscriptions.add(
      this.field.options?.fieldChanges
        ?.asObservable()
        .pipe(
          filter((change: FormlyValueChangeEvent) => {
            if (
              this.isThisExactField(change.field) &&
              change.type === 'valueChanges' &&
              this.field.props['triggerType'] ===
                EGenericInputTriggerType.TRIGGER_BUTTON &&
              change.value
            ) {
              this.formControl.setErrors({
                ...this.formControl.errors,
                notVerifiedByTriggerAction: true,
              });
            }
            return (
              this.isThisExactField(change.field) &&
              change.type === 'expressionChanges' &&
              change['property'] === 'props.runningAction'
            );
          }),
          distinctUntilChanged(
            (a: FormlyValueChangeEvent, b: FormlyValueChangeEvent) =>
              a.value === b.value
          )
        )
        ?.subscribe((change: FormlyValueChangeEvent) => {
          if (
            change.value &&
            this.field.formControl.value &&
            !this.isCurrentlyFetching
          ) {
            this.onDataFetch(this.field.formControl.value);
            return;
          }
          this.field.props['runAction'] = false;
        })
    );

    checkForValidFields(this.field);

    this.fieldsToPopulate = this.field.props?.['populates'] ?? [];
    if (this.fieldsToPopulate?.length > 0) {
      this.subscriptions.add(
        this.field.formControl.valueChanges
          .pipe(
            distinctUntilChanged(
              (a, b) => JSON.stringify(a) === JSON.stringify(b)
            )
          )
          .subscribe({
            next: () => {
              if (this.field.formControl.invalid) {
                resetFieldsToPopulate(
                  this.fieldsToPopulate,
                  this.currentRepeaterForm,
                  this.formStateService
                );
              }
            },
          })
      );
    }
  }

  buildPayload(inputValue: string): Record<string, unknown> {
    const {
      useMultiplePayloadKeys,
      payloadKey,
      payloadKeyMappings,
      triggerType,
    } = this.field.props;

    // Handle non-field-listener cases with existing logic
    if (triggerType !== EGenericInputTriggerType.FIELD_LISTENER) {
      if (!useMultiplePayloadKeys || !payloadKeyMappings?.length) {
        return { [payloadKey]: inputValue };
      }

      const payload: Record<string, unknown> = {};
      payloadKeyMappings.forEach((mapping: any) => {
        if (mapping.isStatic) {
          payload[mapping.key] = mapping.staticValue;
        } else if (mapping.sourceField) {
          const sourceValue = this.findControl(
            mapping.sourceField,
            mapping.searchInRootForm
          )?.value;
          payload[mapping.key] = sourceValue;
        } else {
          payload[mapping.key] = inputValue;
        }
      });
      return payload;
    }

    if (!useMultiplePayloadKeys) {
      // Use scoped control finder
      const control = this.findControl(payloadKey);
      return { [payloadKey]: control?.value };
    }

    const payload: Record<string, unknown> = {};
    payloadKeyMappings.forEach((mapping: any) => {
      if (mapping.isStatic) {
        payload[mapping.key] = mapping.staticValue;
      } else if (mapping.sourceField) {
        const control = this.findControl(
          mapping.sourceField,
          mapping.searchInRootForm
        );
        payload[mapping.key] = control?.value;
      }
    });

    return payload;
  }

  onDataFetch(inputValue: string) {
    if (this.isCurrentlyFetching) {
      return;
    }

    let { url } = this.field.props;
    const { endpointCode } = this.field.props;

    if (!url || !endpointCode) {
      throw new Error('url and endpointCode properties are required');
    }

    if (this.field.props['useBaseUrl']) {
      url = updateUrlWithApiGatewayBaseUrl(url, this.environment);
    }

    const payload = this.buildPayload(inputValue);

    const params = {
      endpointCode,
      callerId: uuidv4(),
      payload,
      requester: uuidv4(),
    };

    this.isCurrentlyFetching = true;
    this.statusClass = GenericInputStatusEnum.FETCHING;
    this.formControl.setErrors(null);
    this.formControl.updateValueAndValidity();
    this.formStateService.clearGlobalError(this.field);
    this.subscriptions.add(
      this.http
        .post<Record<string, unknown>>(url, params)
        .pipe(
          finalize(() => {
            this.isCurrentlyFetching = false;
            if (
              this.field.props?.['triggerType'] ===
              EGenericInputTriggerType.TRIGGER_BUTTON
            ) {
              this.field.props['runAction'] = false;
            }
          })
        )
        .subscribe({
          next: (res: Record<string, unknown>) => {
            this.statusClass = GenericInputStatusEnum.SUCCESS;
            const data = res['data'] as Record<string, unknown>;
            const response = data['response'] as string;

            this.formStateService.saveFetchedDataKeyInFormState(
              this.field,
              response
            );

            if (
              this.fieldsToPopulate.length > 0 &&
              this.field.formControl.valid
            ) {
              // Run transformToArray to get other inputs is populated
              // This allows hide expressions to evaluate correctly before populateFormFields runs
              const transformConfig = this.field.props?.[
                'transformToArray'
              ] as TransformToArrayConfig;
              if (transformConfig?.enabled) {
                this.handleTransformToArray(response, transformConfig);
              }

              // Trigger change detection to update hide expressions
              this.field.options?.detectChanges?.(this.field);

              // Populate the form fields
              populateFormFields(
                response,
                this.field,
                this.currentRepeaterForm,
                this.formStateService
              );
            } else {
              this.field.options?.detectChanges?.(this.field);
            }
            this.formControl.setErrors(null);
            this.field.formControl.updateValueAndValidity();
            this.cd.detectChanges();

            // Emit fetch success event for chaining
            this.formStateService.emitFetchSuccess({
              fetchKey: this.field.key as string,
              componentId: this.componentId,
              response: res,
            });
          },
          error: error => {
            const { errors, statusClass } = handleErrorMapping(
              error?.error || error,
              this.field.props['errorMapping'],
              'invalidGenericInput',
              this.field,
              this.formControl,
              true
            );

            const errorKey = Object.keys(errors)[0];
            const errorMessage =
              errors[errorKey]?.message ||
              this.field.validation?.messages?.[errorKey];

            if (this.field?.props?.['showGlobalError'] && errorMessage)
              this.formStateService.addGlobalError(this.field, errorMessage);

            this.statusClass = statusClass;
            this.formControl.setErrors(errors);
            this.cd.detectChanges();
          },
        })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /**
   * Handle the transformToArray configuration
   * Transforms a single fetch result into an array format compatible with custommultiselectdatafetch
   */
  private handleTransformToArray(
    response: string | object,
    config: TransformToArrayConfig
  ): void {
    try {
      // Parse response if it's a string
      let parsedResponse: Record<string, unknown>;
      if (typeof response === 'string') {
        parsedResponse = JSON.parse(response);
      } else {
        parsedResponse = response as Record<string, unknown>;
      }

      // Extract data from the configured path
      const sourceData = config.dataPath
        ? this.getNestedValue(parsedResponse, config.dataPath)
        : parsedResponse;

      if (!sourceData) {
        console.warn('transformToArray: No data found at specified path');
        return;
      }

      // Build the transformed object using mappings
      const transformedItem: Record<string, unknown> = {};

      config.mappings.forEach((mapping: TransformFieldMapping) => {
        const value = this.getNestedValue(sourceData, mapping.sourcePath);
        transformedItem[mapping.targetKey] = this.applyFormatter(
          value,
          mapping.formatter
        );
      });

      // Add input values if configured
      if (config.includeInputValues) {
        const inputValues: Record<string, unknown> = {};

        if (config.inputValueMappings?.length) {
          config.inputValueMappings.forEach(mapping => {
            inputValues[mapping.targetKey] = this.formControl.value;
          });
        } else {
          // Default: include the current field value
          inputValues[this.field.key as string] = this.formControl.value;
        }

        transformedItem['__inputValues'] = inputValues;
      }

      // Wrap in array (single item)
      const resultArray = [transformedItem];

      // Find and update the target field - search from root form
      const targetControl = this.findTargetControl(config.targetField);

      if (targetControl) {
        targetControl.setValue(resultArray);
        targetControl.updateValueAndValidity();

        // Save to form state for preview
        this.saveTransformDataToFormState(config, resultArray);
      } else {
        console.warn(
          `transformToArray: Target field '${config.targetField}' not found`
        );
      }
    } catch (error) {
      console.error('transformToArray: Error transforming data', error);
    }
  }

  /**
   * Find target control by searching from the root form
   * This is needed because the target field might be a hidden sibling
   */
  private findTargetControl(targetKey: string): AbstractControl | null {
    // Try to find using the formly field's formControl
    const targetField = this.findTargetFormlyField(targetKey);
    if (targetField?.formControl) {
      return targetField.formControl;
    }

    // Search in current form (same block level)
    const currentFormControl = this.form.get(targetKey);
    if (currentFormControl) {
      return currentFormControl;
    }

    // Search from parent form
    if (this.field.parent?.formControl) {
      const parentControl = (
        this.field.parent.formControl as AbstractControl
      ).get?.(targetKey);
      if (parentControl) {
        return parentControl;
      }
    }

    // Get root form and search recursively
    let rootForm = this.form;
    while (rootForm.parent) {
      rootForm = rootForm.parent;
    }

    return findFormControl(rootForm, targetKey);
  }

  /**
   * Save transformed data to form state for preview display
   */
  private saveTransformDataToFormState(
    config: TransformToArrayConfig,
    resultArray: Record<string, unknown>[]
  ): void {
    const targetField = this.findTargetFormlyField(config.targetField);

    if (targetField) {
      // Update target field props with columns and previewMetadata for preview display
      // This is needed because the preview util reads from field.props
      if (config.columns?.length) {
        targetField.props = targetField.props || {};
        targetField.props['columns'] = config.columns;
      }

      if (config.previewMetadata?.length) {
        targetField.props = targetField.props || {};
        targetField.props['previewMetadata'] = config.previewMetadata;
      }

      // Save fetched data
      this.formStateService.saveFetchedDataKeyInFormState(
        targetField,
        resultArray,
        EIremboFormlyFormStateKeys.FETCHED_DATA,
        false
      );

      // Save columns configuration if provided
      if (config.columns?.length) {
        this.formStateService.saveFetchedDataKeyInFormState(
          targetField,
          config.columns,
          EIremboFormlyFormStateKeys.FETCHED_COLUMNS_DATA,
          false
        );
      }

      // Compute and save preview metadata if configured
      if (config.previewMetadata?.length) {
        const metadata = this.computeTransformPreviewMetadata(
          config,
          resultArray
        );
        this.formStateService.saveFetchedDataKeyInFormState(
          targetField,
          metadata,
          EIremboFormlyFormStateKeys.FETCHED_METADATA,
          false
        );
      }
    }
  }

  /**
   * Compute preview metadata from transformed data
   */
  private computeTransformPreviewMetadata(
    config: TransformToArrayConfig,
    resultArray: Record<string, unknown>[]
  ): Record<string, unknown> {
    const metadata: Record<string, unknown> = {};

    config.previewMetadata?.forEach(metaConfig => {
      const sourceField = metaConfig.sourceField || '';

      switch (metaConfig.type) {
        case 'count':
          metadata[metaConfig.key] = resultArray.length;
          break;
        case 'sum':
          metadata[metaConfig.key] = resultArray.reduce((sum, item) => {
            return sum + (Number(this.getNestedValue(item, sourceField)) || 0);
          }, 0);
          break;
        case 'first':
          metadata[metaConfig.key] =
            resultArray.length > 0
              ? this.getNestedValue(resultArray[0], sourceField)
              : null;
          break;
        case 'input':
          metadata[metaConfig.key] = this.formControl.value || null;
          break;
        case 'static':
          metadata[metaConfig.key] = metaConfig.value;
          break;
      }
    });

    return metadata;
  }

  /**
   * Find the target formly field by key
   */
  private findTargetFormlyField(targetKey: string): FormlyFieldConfig | null {
    const searchField = (
      fields: FormlyFieldConfig[]
    ): FormlyFieldConfig | null => {
      for (const field of fields) {
        if (field.key === targetKey) {
          return field;
        }
        if (field.fieldGroup?.length) {
          const found = searchField(field.fieldGroup);
          if (found) return found;
        }
      }
      return null;
    };

    // Navigate to root field using explicit cast
    let rootField = this.field as FormlyFieldConfig;
    while (rootField.parent) {
      rootField = rootField.parent as FormlyFieldConfig;
    }

    if (rootField.fieldGroup) {
      return searchField(rootField.fieldGroup);
    }

    return null;
  }

  /**
   * Clear the target field when fetch fails
   */
  private clearTransformTargetField(targetField: string): void {
    const targetControl = this.findControl(targetField);
    if (targetControl) {
      targetControl.setValue([]);
      targetControl.updateValueAndValidity();
    }
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return obj;
    return path.split('.').reduce((current, prop) => current?.[prop], obj);
  }

  /**
   * Apply formatter to value
   */
  private applyFormatter(value: any, formatter?: string): any {
    if (!formatter || value === null || value === undefined) {
      return value;
    }

    switch (formatter) {
      case 'date':
        return this.formatDate(value);
      case 'currency':
        return typeof value === 'number' ? value : parseFloat(value) || 0;
      case 'array-first-name':
        return Array.isArray(value) && value.length > 0
          ? value[0]?.name
          : 'N/A';
      default:
        return value;
    }
  }

  /**
   * Format date string for display
   */
  private formatDate(dateString: string | null | undefined): string {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return `${(date.getMonth() + 1).toString().padStart(2, '0')}/${date
        .getDate()
        .toString()
        .padStart(2, '0')}/${date.getFullYear()}`;
    } catch {
      return dateString;
    }
  }
}
